import React from 'react';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import dayjs from 'dayjs';
import 'dayjs/locale/es';
import { Hit } from '@algolia/client-search';
import { Card, formatCurrency, Table } from '@avila-tek/ui/src';
import { createColumnHelper } from '@tanstack/react-table';
import { useRouter } from 'next/router';
import { useAlgoliaSearch, useUser } from '../../hooks';
import { AlgoliaHitToObject, statusColor } from '../../lib';
import { Company, Order, PageInfo, Pagination } from '../../models';
import { roles } from '../../static';
import { orderStatusTranslate } from '../../static/orderStatus';
import { Badge } from '../shared';

dayjs.extend(localizedFormat);
dayjs.locale('es');

const columnHelper = createColumnHelper<Order>();

const columns = [
  columnHelper.accessor('code', {
    header: 'Código',
  }),

  columnHelper.accessor('createdAt', {
    header: 'Fecha',
    cell: (info) => `${dayjs(info.row.original.createdAt).format('L')}`,
  }),

  columnHelper.accessor('user', {
    id: 'user',
    header: 'Cliente',
    cell: (info) =>
      `${info.row.original.user?.firstName} ${info.row.original.user?.lastName}`,
  }),

  columnHelper.accessor('user', {
    header: 'Compañia',
    cell: (info) =>
      `${(info.row.original.user?.company as Company)?.name ?? ''}`,
  }),

  columnHelper.accessor('total', {
    header: 'Total',
    cell: (info) => `$${formatCurrency(info.row.original.total)} `,
  }),

  columnHelper.accessor('status', {
    header: 'Estatus',
    cell: (info) => (
      <Badge
        bgClassName={`${statusColor(info.row.original.status, 'bg')}`}
        textClassName={`${statusColor(info.row.original.status, 'text')}`}
        text={orderStatusTranslate[info.row.original.status]}
      />
    ),
  }),
];

interface OrderTableProps {
  data: Pagination<Order>;
  pagination: Partial<PageInfo>;
  setPagination: React.Dispatch<React.SetStateAction<Partial<PageInfo>>>;
  userId?: string;
}

function OrderTable({
  data,
  pagination,
  setPagination,
  userId = '',
}: OrderTableProps) {
  const router = useRouter();
  const [user] = useUser();

  const [searchText, setSearchText] = React.useState<string | number>('');
  const [ordersData, setOrdersData] = React.useState<Order[]>(data.items);
  const [pageInfoAlgolia, setPageInfoAlgolia] = React.useState<PageInfo>({
    currentPage: 0,
    hasNextPage: false,
    hasPreviousPage: false,
    itemCount: 0,
    pageCount: 0,
    perPage: 10,
  });

  const { userType, company } = user;

  const allyFilter = company && userType === roles.ally;
  const companyFilter = company
    ? `companies._id:"${(company as Company)._id}"`
    : '';
  const userIdFilter = userId ? `user._id:"${userId}"` : '';

  const { fetchData } = useAlgoliaSearch<Order>({
    pageInfo: pageInfoAlgolia,
    setPageInfo: setPageInfoAlgolia,
    restrictSearchableAttributes: [
      'user.firstName',
      'user.lastName',
      'user.company.name',
      'total',
      'createdAt',
      'code',
    ],
    index: 'orders',
    filters: allyFilter ? companyFilter : userIdFilter,
  });

  const memoizedSyncData = React.useCallback(async () => {
    if (searchText) {
      const { pageInfo, items } = await fetchData({
        searchText: searchText.toString(),
        newPage: pageInfoAlgolia.currentPage,
      });
      setOrdersData(AlgoliaHitToObject<Order>(items as Hit<Order>[]));
      setPageInfoAlgolia(pageInfo);
    } else {
      setOrdersData(data.items);
    }
  }, [searchText, pageInfoAlgolia?.currentPage, pagination]);

  React.useEffect(() => {
    memoizedSyncData();
  }, [searchText, memoizedSyncData]);

  const visibility = {
    user: user?.userType === roles.admin,
    company: user?.userType === roles.admin || user?.userType === roles.ally,
  };

  const onClickRow = (e: React.MouseEvent<HTMLTableRowElement, MouseEvent>) => {
    e.preventDefault();
    const { id } = e.currentTarget.dataset;
    router.push(`/app/orders/${id}`);
  };

  const handleGoToPage = (value: number) => {
    if (searchText) {
      setPageInfoAlgolia({ ...pageInfoAlgolia, currentPage: value - 1 });
    } else {
      setPagination({ ...pagination, currentPage: value === 0 ? 1 : value });
    }
  };

  return (
    <Card className="overflow-x-auto p-5">
      <Table<Order>
        data={ordersData}
        columns={columns}
        href="/app/orders"
        visibility={visibility}
        manualPagination
      >
        <Table.Header className="p-6 w-full flex justify-between">
          <Table.SearchInput
            manualFilter
            value={searchText.toString()}
            onChange={(value) => {
              setSearchText(value);
            }}
          />
        </Table.Header>
        <Table.Content<Order> onClickRow={onClickRow} />
        <Table.Footer>
          <Table.Pagination
            handleGoToPage={handleGoToPage}
            paginationInfo={{
              count: data.count,
              pageInfo: searchText
                ? {
                    ...pageInfoAlgolia,
                    currentPage: pageInfoAlgolia.currentPage + 1,
                  }
                : data.pageInfo,
            }}
            className="ml-auto"
          />
        </Table.Footer>
      </Table>
    </Card>
  );
}

export default OrderTable;
